/* Standalone public/restricted-test transport for the Shopify RFQ workspace.
 * No endpoint, access code, token, cookies, storage, or automatic requests are embedded.
 * Loading this asset only registers a factory. The workspace opts in explicitly. */
(function (global) {
'use strict';
const categoryCodes = ['appearance', 'color', 'dimensions', 'environment', 'assembly', 'inspection'];
const trim = value => String(value ?? '').trim();
const serializeQuantity = raw => {
  const value = trim(raw);
  if (!value) return null;
  const number = Number(value);
  // Keep invalid nonempty input intact so JSON cannot turn NaN/Infinity into
  // an optional empty quantity. The service returns the field-level error.
  return /^\d+$/.test(value) && Number.isSafeInteger(number) && number > 0 ? number : value;
};
const hasReferenceContent = item => [item.name, item.description, item.quantity, item.material, item.other, item.methodName, item.standardVersion, item.methodFlex].some(value => trim(value)) || item.files.length > 0;

/** Convert only the active draft to the canonical API envelope. Upload IDs are server-issued. */
function serializeRequest(draft, uploadedIds) {
  const discussion = draft.intent === 'advice';
  const fileId = file => {
    const id = uploadedIds.get(file.id);
    if (!id) throw new Error(`File is not saved locally: ${file.name}`);
    return id;
  };
  const items = (discussion ? draft.items.filter(hasReferenceContent) : draft.items).map(item => {
    const value = {
      id: item.id, name: trim(item.name), description: trim(item.description),
      quantity: serializeQuantity(item.quantity),
      material: trim(item.material), fileIds: item.files.map(fileId),
      notes: trim(item.other),
      specifiedMethod: { name: trim(item.methodName), version: trim(item.standardVersion), policy: item.methodFlex || '' }
    };
    if (!discussion) {
      value.timing = { planConfirmationDate: trim(item.planConfirmationDate), dispatchDate: trim(item.dispatchDate) };
      value.expertAdvice = { requested: Boolean(item.adviceRequested), question: item.adviceRequested ? trim(item.adviceQuestion) : '' };
      if (!['unsure', 'M11'].includes(item.process)) {
        const selection = item.materialSelection || {};
        const mode = trim(selection.mode);
        const selectedFile = item.files.find(file => file.id === selection.fileId);
        value.material = '';
        value.materialSelection = {
          mode, optionId: mode === 'catalog' ? trim(selection.optionId) : '',
          detail: ['catalog', 'other'].includes(mode) ? trim(selection.detail) : '',
          fileId: mode === 'file' && selectedFile ? fileId(selectedFile) : null
        };
      }
      value.requirementType = { 'Manufacture a new part': 'manufacture_new', 'Finish or modify existing parts': 'finish_existing' }[item.delivery] || '';
      const specific = item.process === 'unsure' ? null : item.processRoutes?.[item.process] || null;
      value.process = { main: item.process, specific, policy: specific && specific !== 'unsure' ? item.processFlex || null : null };
      value.requirements = Object.keys(item.extra).map(key => {
        const source = item.extraSources?.[key] || { mode: 'text' };
        const localFile = item.files.find(file => file.id === source.fileId);
        return {
          category: categoryCodes[Number(key)], sourceMode: source.mode,
          text: source.mode === 'text' ? trim(item.extra[key]) : null,
          fileId: source.mode === 'file' && localFile ? fileId(localFile) : null
        };
      });
    }
    return value;
  });
  return {
    schemaVersion: 'rfq.v2', clientRequestId: draft.clientRequestId,
    ...(draft.serviceTermsAcceptance?.accepted === true ? { serviceTermsAcceptance: { accepted: true, version: trim(draft.serviceTermsAcceptance.version), contentHash: trim(draft.serviceTermsAcceptance.contentHash) } } : {}),
    requestType: discussion ? 'discussion' : 'quote', expertAdviceRequested: false,
    contact: Object.fromEntries(['name', 'email', 'company', 'phone'].map(key => [key, trim(draft.contact[key])])),
    project: {
      ...(discussion ? { question: trim(draft.project.question), topics: [...draft.project.topics] } : {}),
      ...(discussion ? { discussionTiming: {
        adviceDate: trim(draft.project.discussionTiming?.adviceDate),
        quotationDate: trim(draft.project.discussionTiming?.quotationDate),
        partsArrivalDate: trim(draft.project.discussionTiming?.partsArrivalDate)
      } } : { shippingPreference: {
        service: trim(draft.project.shippingPreference?.service),
        carrier: trim(draft.project.shippingPreference?.carrier)
      } }),
      deliveryRegion: trim(draft.project.deliveryRegion),
      deliveryCity: trim(draft.project.deliveryRegion) ? trim(draft.project.deliveryCity) : '',
      deliveryPostal: trim(draft.project.deliveryRegion) ? trim(draft.project.deliveryPostal) : ''
    },
    items, projectFileIds: draft.project.files.map(fileId)
  };
}

function mapServerErrors(errors, draft) {
  const submittedItems = draft.intent === 'advice' ? draft.items.filter(hasReferenceContent) : draft.items;
  return errors.map(error => {
    const [scope, key, nested, tail] = String(error.path || '').split('.');
    let id = 'project', field = null;
    if (scope === 'serviceTermsAcceptance') { id = 'terms'; field = 'serviceTermsAcceptance'; }
    if (error.path === 'items' && error.code === 'required' && !submittedItems.length) field = 'items';
    if (scope === 'contact' || scope === 'project') { id = scope; field = key; }
    if (scope === 'project' && key === 'discussionTiming') field = nested || 'adviceDate';
    if (scope === 'project' && key === 'shippingPreference') field = ({ service: 'shippingService', carrier: 'shippingCarrier' })[nested] || 'shippingService';
    if (scope === 'projectFileIds') field = 'files';
    if (scope === 'items' && submittedItems[Number(key)]) {
      const item = submittedItems[Number(key)]; id = item.id;
      field = nested === 'requirementType' ? 'delivery' : nested === 'process' ? ({ main: 'process', specific: 'processRoute', policy: 'processFlex' }[tail] || 'process') : nested === 'fileIds' ? 'files' : nested;
      if (nested === 'timing') field = tail || 'planConfirmationDate';
      if (nested === 'expertAdvice') field = tail === 'requested' ? 'adviceRequested' : 'adviceQuestion';
      if (nested === 'materialSelection') field = ({ mode: 'materialMode', optionId: 'materialMode', detail: 'materialDetail', fileId: 'materialFileId' })[tail] || 'materialMode';
      if (nested === 'requirements') field = `extra-${Object.keys(item.extra)[Number(tail)] || 0}`;
    }
    return { id, field, path: error.path, code: error.code, message: error.message, summary: `${scope === 'serviceTermsAcceptance' ? 'Service terms' : scope === 'contact' ? 'Contact' : scope === 'items' ? 'Item' : 'Project'}: ${error.message}` };
  });
}

function create(config = {}) {
  if (typeof config.baseUrl !== 'string' || !config.baseUrl.trim()) throw new Error('The request service endpoint is not configured.');
  const base = new URL(config.baseUrl, global.location?.href);
  const loopback = ['127.0.0.1', 'localhost', '[::1]'].includes(base.hostname);
  if ((base.protocol !== 'https:' && !(base.protocol === 'http:' && loopback)) || base.username || base.password || base.search || base.hash) {
    throw new Error('Use an HTTPS request-service base URL without credentials or query parameters.');
  }
  if (!base.pathname.endsWith('/')) base.pathname += '/';
  const endpoint = path => {
    if (!/^[a-z][a-z0-9/-]*$/i.test(path)) throw new Error('Invalid request-service route.');
    return new URL(path, base).href;
  };
  const routes = {
    session: endpoint(config.paths?.session || 'session'),
    uploads: endpoint(config.paths?.uploads || 'uploads'),
    requests: endpoint(config.paths?.requests || 'requests')
  };
  const expectedMode = config.expectedMode || 'restricted-test';
  const expectedStatus = config.expectedStatus || 'received_test';
  const publicMode = expectedMode === 'public';
  if (!['restricted-test','public'].includes(expectedMode) || expectedStatus !== (publicMode ? 'received' : 'received_test')) throw new Error('The request service mode and receipt status do not match.');
  if (publicMode && base.pathname !== '/api/intake/v2/public/') throw new Error('Public submissions require the public request service endpoint.');
  const fetchRequest = config.fetch || global.fetch.bind(global);
  let session, sessionPending;
  let uploadCache = new WeakMap();
  const inflight = new WeakMap();
  const saveAttempts = new WeakMap();
  function resetSession() { session = undefined; sessionPending = undefined; uploadCache = new WeakMap(); }
  async function request(url, options, timeout) {
    let response;
    try {
      response = await fetchRequest(url, { ...options, credentials: 'omit', redirect: 'error', cache: 'no-store', signal: AbortSignal.timeout(timeout) });
    } catch (cause) {
      const timedOut = ['TimeoutError', 'AbortError'].includes(cause?.name);
      const error = new Error(timedOut
        ? 'The request service took too long to respond. Submission is not confirmed. Retry with this request.'
        : 'The request service could not be reached. Your input and selected files are still here.');
      error.code = timedOut ? 'service_timeout' : 'service_unavailable';
      throw error;
    }
    let result;
    try { result = await response.json(); }
    catch {
      const error = new Error('The request service returned an unreadable response. Submission is not confirmed.');
      error.status = response.status; error.code = 'invalid_response'; throw error;
    }
    if (!response.ok) {
      if ([401, 403].includes(response.status)) resetSession();
      if (result.code === 'file_integrity') uploadCache = new WeakMap();
      const error = new Error(result.error || result.message || `The request could not be saved (HTTP ${response.status}).`);
      error.status = response.status; error.code = result.code; error.errors = result.errors;
      const retryAfter = Number(response.headers.get('retry-after'));
      if (response.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) error.retryAfterSeconds = retryAfter;
      throw error;
    }
    return result;
  }
  async function getSession() {
    if (session && session.expiresAt <= Date.now()) resetSession();
    if (session) return session;
    if (!sessionPending) sessionPending = (async () => {
      // A caller may collect a temporary tester code at runtime. It is sent only
      // to the configured service and is never kept in DOM, URLs or storage.
      let result;
      if (config.getSession) result = await config.getSession();
      else {
        if (!publicMode && !config.getSessionInput) throw new Error('Open the restricted test entry and enter its access code before submitting.');
        const supplied = config.getSessionInput ? await config.getSessionInput() : {};
        const input = publicMode ? {purpose:'new_request',website:trim(supplied?.website)} : supplied;
        if (!input || typeof input !== 'object' || !Object.keys(input).length) throw new Error('Test access was not provided. Your request is still here.');
        result = await request(routes.session, {
          method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input)
        }, 15000);
      }
      const expiry = typeof result?.expiresAt === 'number'
        ? result.expiresAt * (result.expiresAt < 1e12 ? 1000 : 1) : Date.parse(result?.expiresAt);
      if (!result || (publicMode ? result.mode !== 'public' : result.mode && result.mode !== expectedMode) || (!publicMode && result.externalTransports === true && config.allowExternalTransports !== true) || typeof result.sessionToken !== 'string' || !result.sessionToken || typeof result.csrfToken !== 'string' || !result.csrfToken || !Number.isFinite(expiry) || expiry <= Date.now()) {
        throw new Error(publicMode ? 'The request service could not start a submission session. Nothing was submitted.' : 'The request service did not confirm an isolated test session. Nothing was submitted.');
      }
      session = { sessionToken: result.sessionToken, csrfToken: result.csrfToken, expiresAt: expiry };
      return session;
    })().finally(() => { sessionPending = undefined; });
    return sessionPending;
  }
  function authorization(activeSession, contentType) {
    return { 'content-type': contentType, authorization: `Bearer ${activeSession.sessionToken}`, 'x-csrf-token': activeSession.csrfToken };
  }
  async function linkSubmittedRequest(requestId, sessionToken) {
    if (!publicMode || config.accountAutoLink !== true) return 'not_checked';
    // The Shopify app proxy signs the account identity. The intake session proves
    // ownership of this one newly submitted request. Neither email nor a page
    // supplied customer ID is accepted as a substitute for either proof.
    try {
      const accountPath='/apps/rfq/intake';
      const nonceResponse=await fetchRequest(accountPath+'/session',{
        method:'GET',credentials:'same-origin',redirect:'error',cache:'no-store',signal:AbortSignal.timeout(8000)
      });
      if(nonceResponse.status===401)return 'sign_in_required';
      if(!nonceResponse.ok)return 'retry';
      const nonce=await nonceResponse.json();
      if(typeof nonce.csrfToken!=='string'||!nonce.csrfToken||Date.parse(nonce.expiresAt)<=Date.now())return 'retry';
      const response=await fetchRequest(accountPath+'/submission-link',{
        method:'POST',credentials:'same-origin',redirect:'error',cache:'no-store',signal:AbortSignal.timeout(8000),
        headers:{'content-type':'application/json'},
        body:JSON.stringify({requestId,sessionToken,csrfToken:nonce.csrfToken})
      });
      if(response.status===401)return 'sign_in_required';
      if(!response.ok)return 'retry';
      const linked=await response.json();
      return linked?.requestId===requestId&&linked?.linked===true?'linked':'retry';
    }catch{return 'retry';}
  }
  async function retryAccountLink(receipt) {
    if(!receipt||!/^RFQ-\d{8}-[A-F0-9]{10}$/.test(receipt.requestId||''))return 'retry';
    const activeSession=session&&session.expiresAt>Date.now()?session:null;
    return activeSession?linkSubmittedRequest(receipt.requestId,activeSession.sessionToken):'retry';
  }
  async function performSubmit(draft, onProgress) {
    if (!draft.clientRequestId) draft.clientRequestId = global.crypto.randomUUID();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(draft.clientRequestId)) {
      throw new Error('The request identifier is invalid. Keep this page open and contact support.');
    }
    const previous = saveAttempts.get(draft);
    const pendingBody = previous?.clientRequestId === draft.clientRequestId ? previous.body : null;
    const items = draft.intent === 'advice' ? draft.items.filter(hasReferenceContent) : draft.items;
    const files = pendingBody ? [] : [{ id: 'project', files: draft.project.files }, ...items].flatMap(owner => owner.files.map(selected => ({ ownerItemId: owner.id, selected })));
    if (files.some(({ selected }) => !(selected.file instanceof global.File))) throw new Error('A selected file is a preview placeholder. Select the real file before submitting.');
    onProgress('Connecting to the request service…');
    const activeSession = await getSession();
    const attempted = saveAttempts.get(draft);
    if (attempted?.clientRequestId === draft.clientRequestId && attempted.sessionToken !== activeSession.sessionToken) {
      const error = new Error('Your submission session changed after a save attempt. The previous save result must be checked by staff before retrying. Your input and files are still here.');
      error.code = 'session_changed_after_submission';
      throw error;
    }
    const ids = new Map();
    for (const [index, { selected, ownerItemId }] of files.entries()) {
      const cacheKey = `${draft.clientRequestId}:${ownerItemId}:${selected.id}`;
      let cached = uploadCache.get(selected.file)?.get(cacheKey);
      if (!cached) {
        onProgress(`Uploading file ${index + 1} of ${files.length}: ${selected.name}`);
        const query = new URLSearchParams({ clientRequestId: draft.clientRequestId, ownerItemId, name: selected.name });
        const result = await request(`${routes.uploads}?${query}`, {
          method: 'POST', headers: authorization(activeSession, 'application/octet-stream'), body: selected.file
        }, 120000);
        if (!result.file?.id || typeof result.file.id !== 'string') throw new Error('The file service did not confirm the upload. Retry with this request.');
        cached = { id: result.file.id };
        if (!uploadCache.has(selected.file)) uploadCache.set(selected.file, new Map());
        uploadCache.get(selected.file).set(cacheKey, cached);
      } else onProgress(`File ${index + 1} of ${files.length} already uploaded: ${selected.name}`);
      ids.set(selected.id, cached.id);
    }
    onProgress('Saving your request…');
    // A lost response may already have committed. Keep the exact envelope, including
    // the accepted terms version, until a receipt or a definite rejection arrives.
    const body = pendingBody || JSON.stringify(serializeRequest(draft, ids));
    saveAttempts.set(draft, { clientRequestId: draft.clientRequestId, sessionToken: activeSession.sessionToken, body });
    let result;
    try {
      result = await request(routes.requests, {
        method: 'POST', headers: authorization(activeSession, 'application/json'), body
      }, 30000);
    } catch (error) {
      if ((!pendingBody && ([400, 401, 403, 422, 429].includes(error.status) || error.code === 'file_integrity')) || ['service_terms_required', 'service_terms_changed'].includes(error.code)) saveAttempts.delete(draft);
      throw error;
    }
    if (result.status !== expectedStatus || !/^RFQ-\d{8}-[A-F0-9]{10}$/.test(result.requestId || '') || !Number.isFinite(Date.parse(result.receivedAt))) {
      const error = new Error('The request service did not confirm a saved request. Retry with this request.');
      error.code = 'unconfirmed_receipt'; throw error;
    }
    saveAttempts.delete(draft);
    // Only confirmed receipt data crosses back into the customer UI. Internal
    // URLs, tester capabilities and service configuration are not exposed.
    const accountLink=await linkSubmittedRequest(result.requestId,activeSession.sessionToken);
    return { requestId: result.requestId, receivedAt: result.receivedAt, status: result.status, replayed: result.replayed === true, accountLink };
  }
  function submit(draft, onProgress = () => {}) {
    if (inflight.has(draft)) return inflight.get(draft);
    const promise = performSubmit(draft, onProgress).finally(() => inflight.delete(draft));
    inflight.set(draft, promise);
    return promise;
  }
  const hasPendingSubmission = draft => { const attempt = saveAttempts.get(draft); return Boolean(attempt?.body && attempt.clientRequestId === draft.clientRequestId); };
  return Object.freeze({ submit, retryAccountLink, mapServerErrors, resetSession, hasPendingSubmission });
}

global.ElyRfqApiBridge = Object.freeze({ create, serializeRequest, mapServerErrors });
})(globalThis);
